import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ANIMATION, DIRECTION_CONFIG, type Direction, prefersReducedMotion } from './animation-config'

gsap.registerPlugin(ScrollTrigger)

export interface DirectionalRevealOptions {
  duration?: number
  delay?: number
  stagger?: number
  blur?: number
  distance?: number
  scale?: number
  start?: string
  once?: boolean
}

/**
 * Reveal elements from different directions on scroll
 *
 * @example
 * function ActSection() {
 *   const sectionRef = useDirectionalReveal<HTMLElement>('.reveal', 'left')
 *   return (
 *     <section ref={sectionRef}>
 *       <h2 className="reveal">Act 1</h2>
 *       <p className="reveal">Content here...</p>
 *     </section>
 *   )
 * }
 */
export function useDirectionalReveal<T extends HTMLElement>(
  selector: string,
  direction: Direction = 'bottom',
  options: DirectionalRevealOptions = {}
) {
  const containerRef = useRef<T>(null)
  // Destructure to primitives so the effect deps are stable (an inline `options`
  // object would rebuild the ScrollTrigger every render).
  const {
    duration = ANIMATION.duration.normal,
    delay = 0,
    stagger = ANIMATION.stagger.normal,
    blur = ANIMATION.blur.normal,
    scale = ANIMATION.scale.subtle,
    start = 'top 85%',
    once = true,
  } = options

  useEffect(() => {
    if (!containerRef.current) return
    const container = containerRef.current

    // Reduced motion: show elements at their visible resting state, no animation.
    if (prefersReducedMotion()) {
      container.querySelectorAll(selector).forEach((el) => gsap.set(el, { opacity: 1 }))
      return
    }

    const elements = container.querySelectorAll(selector)
    if (elements.length === 0) return

    const directionOffset = DIRECTION_CONFIG[direction]

    // gsap.context so ctx.revert() restores the inline gsap.set styles on unmount
    // (a bare trigger.kill() leaves elements stuck hidden if unmounted pre-trigger).
    const ctx = gsap.context(() => {
      gsap.set(elements, {
        opacity: 0,
        x: directionOffset.x,
        y: directionOffset.y,
        scale: direction === 'fade' ? scale : 1,
        filter: `blur(${blur}px)`,
      })
      gsap.to(elements, {
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        filter: 'blur(0px)',
        duration,
        delay,
        stagger,
        ease: ANIMATION.ease.reveal,
        scrollTrigger: { trigger: container, start, once },
      })
    }, container)

    return () => ctx.revert()
  }, [selector, direction, duration, delay, stagger, blur, scale, start, once])

  return containerRef
}
